import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Alert,
  Stack,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { CheckCircle, Error, Info } from "@mui/icons-material";
import { themes } from "services/constants";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { useSelector } from "react-redux";
import Papa from "papaparse";
import { showToast } from "services/utils/Status";
import sessionHandling from "services/utils/notificationUtils";

const mapHeaderIndex = (headers, aliases) => {
  const normalized = headers.map((header) =>
    String(header || "")
      .trim()
      .toLowerCase(),
  );
  for (const alias of aliases) {
    const index = normalized.indexOf(alias.toLowerCase());
    if (index !== -1) {
      return index;
    }
  }
  return -1;
};

export const BulkImportReview = ({ data, onBack, onClose, payScheduleId }) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [importStatus, setImportStatus] = useState("pending"); // pending, importing, completed, error
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState([]);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const importStartedRef = useRef(false);
  const totalRecordsRef = useRef(0);

  const setRecordTotal = useCallback((recordCount) => {
    totalRecordsRef.current = recordCount;
    setTotalRecords(recordCount);
  }, []);

  const skippedRows = useMemo(
    () => importResults.filter((result) => result.status === "error"),
    [importResults],
  );
  const hasSkippedRows = skippedRows.length > 0;
  const importHeaderSubtitle = [
    data.componentType?.label,
    data.componentName?.label,
    data.payroll?.label,
  ]
    .filter(Boolean)
    .join(" · ");

  const skippedRowsMessage = errorCount
    ? `${successCount} of ${totalRecords} rows imported. ${errorCount} ${
        errorCount === 1 ? "row was" : "rows were"
      } skipped due to import errors. Please check the details below.`
    : `${totalRecords} rows imported.`;

  const performImport = useCallback(async () => {
    setImportStatus("importing");
    setImportProgress(0);

    try {
      let csvData;
      let idIndex, amountIndex;

      // If preview data is available, use it; otherwise parse CSV
      if (
        data.previewData &&
        Array.isArray(data.previewData) &&
        data.previewData.length > 0
      ) {
        console.log(
          "Using preview data from BulkImportPreview:",
          data.previewData.length,
          "rows",
        );

        // Use the preview data directly - it's already validated
        csvData = data.previewData.map((row) => [
          row.code,
          row.name,
          row.amountLabel,
        ]);

        idIndex = 0;
        amountIndex = 2;
        setRecordTotal(csvData.length);
      } else {
        // Fallback: Parse CSV file
        console.log("Parsing CSV file for import");

        const results = await new Promise((resolve, reject) => {
          Papa.parse(data.csvFile, {
            complete: (results) => resolve(results),
            error: reject,
          });
        });

        const rawHeaders = results.data[0]
          ? results.data[0].map((cell) => String(cell || "").trim())
          : [];

        idIndex = mapHeaderIndex(rawHeaders, [
          "Employee ID",
          "Code",
          "Employee Code",
        ]);
        amountIndex = mapHeaderIndex(rawHeaders, [
          "Amount",
          "Salary Amount",
          "Value",
        ]);

        csvData = results.data
          .slice(1)
          .filter((row) =>
            row.some((cell) => String(cell || "").trim() !== ""),
          );

        setRecordTotal(csvData.length);  
      }

      const importResults = [];
      let success = 0;
      let error = 0;

      // Collect valid rows
      const validEmployees = [];
      for (let i = 0; i < csvData.length; i++) {
        const row = csvData[i];
        const employeeId = String(row[idIndex] ?? row[0] ?? "").trim();
        const rawAmount = String(row[amountIndex] ?? row[2] ?? "").trim();

        // Extract numeric amount value
        const amountValue = Number(rawAmount.replace(/[^0-9.-]+/g, ""));
        const amount = Number.isFinite(amountValue) ? amountValue : null;

        // Local validation
        const errors = [];
        if (!employeeId) errors.push("Employee ID is missing");
        if (amount == null || amount === 0)
          errors.push("Amount cannot be zero");

        if (errors.length > 0) {
          importResults.push({
            row: i + 2,
            status: "error",
            message: errors.join(", "),
            data: row,
          });
          error++;
        } else {
          validEmployees.push({
            employee_id: employeeId,
            amount: amount,
          });
        }

        // Update progress for validation
        const progress = Math.round(((i + 1) / csvData.length) * 50); // First 50% for validation
        setImportProgress(progress);
      }

      // If there are valid employees, send to API
      if (validEmployees.length > 0) {
        try {
          const payload = {
            payroll_id: payScheduleId,
            component_id: data.componentName?.value,
            component_name: data.componentName?.label,
            component_type: data.componentType?.value,
            employees: validEmployees,
          };

          // Call API to import
          const response = await fetch(
            `${domain}payroll/operations/payrollprocessing/payrollruncycle/batch_add_other_earnings/?company_id=${requestCompanyId}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `token ${token}`,
              },
              body: JSON.stringify(payload),
            },
          );

          if (response.ok) {
            const importData = await response.json();
            // Handle rows array from API response
            if (importData.rows && Array.isArray(importData.rows)) {
              importData.rows.forEach((row) => {
                const rowIndex = csvData.findIndex((csvRow, i) => {
                  const employeeId = String(
                    csvRow[idIndex] ?? csvRow[0] ?? "",
                  ).trim();
                  return employeeId === row.employee_id;
                });
                const rowNum = rowIndex + 1;
                const rowData = csvData[rowIndex];
                if (row.status === "error") {
                  importResults.push({
                    row: rowNum,
                    status: "error",
                    message: row.message || "Import failed",
                    data: rowData,
                  });
                  error++;
                } else {
                  importResults.push({
                    row: rowNum,
                    status: "success",
                    message: row.message || "Imported.",
                    data: rowData,
                  });
                  success++;
                }
              });
            } else if (
              importData.employees &&
              Array.isArray(importData.employees)
            ) {
              // Fallback for older response format
              importData.employees.forEach((emp, idx) => {
                const rowIndex = csvData.findIndex((row, i) => {
                  const employeeId = String(
                    row[idIndex] ?? row[0] ?? "",
                  ).trim();
                  return employeeId === emp.employee_id;
                });
                const rowNum = rowIndex + 1;
                const rowData = csvData[rowIndex];
                if (emp.error) {
                  importResults.push({
                    row: rowNum,
                    status: "error",
                    message: emp.reason || "Import failed",
                    data: rowData,
                  });
                  error++;
                } else {
                  importResults.push({
                    row: rowNum,
                    status: "success",
                    message: "Imported.",
                    data: rowData,
                  });
                  success++;
                }
              });
            } else {
              // If no detailed response, assume all valid employees succeeded
              validEmployees.forEach((emp, idx) => {
                const rowIndex = csvData.findIndex((row, i) => {
                  const employeeId = String(
                    row[idIndex] ?? row[0] ?? "",
                  ).trim();
                  return employeeId === emp.employee_id;
                });
                const rowNum = rowIndex + 1;
                const rowData = csvData[rowIndex];
                importResults.push({
                  row: rowNum,
                  status: "success",
                  message: "Imported.",
                  data: rowData,
                });
                success++;
              });
            }
          } else if (response.status === 409) {
            sessionHandling();
            throw new Error("Session expired");
          } else {
            const errorData = await response.json();
            // If API fails, mark all valid employees as error
            validEmployees.forEach((emp, idx) => {
              const rowIndex = csvData.findIndex((row, i) => {
                const employeeId = String(row[idIndex] ?? row[0] ?? "").trim();
                return employeeId === emp.employee_id;
              });
              const rowNum = rowIndex + 1;
              const rowData = csvData[rowIndex];
              importResults.push({
                row: rowNum,
                status: "error",
                message: errorData.message || "Import failed",
                data: rowData,
              });
              error++;
            });
          }
        } catch (err) {
          // If API call fails, mark all valid employees as error
          validEmployees.forEach((emp, idx) => {
            const rowIndex = csvData.findIndex((row, i) => {
              const employeeId = String(row[idIndex] ?? row[0] ?? "").trim();
              return employeeId === emp.employee_id;
            });
            const rowNum = rowIndex + 1;
            const rowData = csvData[rowIndex];
            importResults.push({
              row: rowNum,
              status: "error",
              message: err.message || "Import failed",
              data: rowData,
            });
            error++;
          });
        }
      }

      // Update progress to 100%
      setImportProgress(100);

      setImportResults(importResults);
      setSuccessCount(success);
      setErrorCount(error);
      setImportStatus("completed");
    } catch (error) {
      console.error('[Bulkimportreview] operation failed:', error);
      showToast("error", "Couldn't complete the import. Please try again.");
      setImportStatus("error");
      setErrorCount(totalRecordsRef.current);
    }
  }, [
    data.componentName?.label,
    data.componentName?.value,
    data.componentType?.value,
    data.csvFile,
    data.previewData,
    domain,
    payScheduleId,
    setRecordTotal,
    token, requestCompanyId
  ]);

  useEffect(() => {
    // Auto-start import when component mounts (only once)
    if (!importStartedRef.current) {
      importStartedRef.current = true;
      performImport();
    }
  }, [performImport]); // Include performImport but the ref prevents multiple calls

  return (
    <Box sx={{ p: 2 }}>
      {/* Import Status */}
      <Box sx={{ mb: 3 }}>
        <Stack spacing={2}>
          {/* Progress Bar */}
          {importStatus === "importing" && (
            <>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Importing data...
                  </Typography>
                  <Typography variant="caption" sx={{ color: themes?.primary }}>
                    {importProgress}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={importProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "rgba(0, 212, 170, 0.1)",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: themes?.primary,
                    },
                  }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Processing {importProgress}% of {totalRecords} records
              </Typography>
            </>
          )}

          {/* Completed Status */}
          {importStatus === "completed" && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Import complete
                </Typography>
                {importHeaderSubtitle && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {importHeaderSubtitle}
                  </Typography>
                )}
              </Box>

              <Alert
                severity={hasSkippedRows ? "warning" : "success"}
                icon={hasSkippedRows ? <Info /> : <CheckCircle />}
                sx={{
                  backgroundColor: hasSkippedRows
                    ? "rgba(255, 193, 7, 0.12)"
                    : "rgba(76, 175, 80, 0.12)",
                  mb: 3,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {hasSkippedRows
                    ? `Import completed with ${errorCount} skipped ${errorCount === 1 ? "row" : "rows"}`
                    : "Import completed."}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {skippedRowsMessage}
                </Typography>
              </Alert>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 2,
                  mb: 3,
                }}
              >
                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    backgroundColor: "rgba(0, 212, 170, 0.1)",
                    border: `1px solid rgba(0, 212, 170, 0.3)`,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#009688" }}
                  >
                    {successCount}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                  >
                    Imported
                  </Typography>
                </Paper>

                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    backgroundColor: "rgba(244, 67, 54, 0.1)",
                    border: `1px solid rgba(244, 67, 54, 0.3)`,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#f44336" }}
                  >
                    {errorCount}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                  >
                    Skipped
                  </Typography>
                </Paper>

                <Paper
                  sx={{
                    p: 3,
                    textAlign: "center",
                    backgroundColor: "rgba(241, 243, 246, 0.85)",
                    border: `1px solid rgba(221, 224, 228, 0.9)`,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: themes?.blackColor }}
                  >
                    {totalRecords}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                  >
                    Total rows
                  </Typography>
                </Paper>
              </Box>
            </>
          )}

          {/* Error Status */}
          {importStatus === "error" && (
            <Alert severity="error" icon={<Error />}>
              <Typography variant="body2">
                An error occurred during import. Please try again.
              </Typography>
            </Alert>
          )}
        </Stack>
      </Box>

      {/* All Imported Data Table */}
      {importStatus === "completed" && importResults.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Import Details ({importResults.length} rows)
          </Typography>
          <TableContainer
            component={Paper}
            sx={{ maxHeight: 400, borderRadius: 2 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      color: themes?.generalText || "#2D3748",
                      fontWeight: 600,
                      backgroundColor: themes?.sidebarDivider,
                      width: 50,
                    }}
                  >
                    S.No
                  </TableCell>
                  <TableCell
                    sx={{
                      color: themes?.generalText || "#2D3748",
                      fontWeight: 600,
                      backgroundColor: themes?.sidebarDivider,
                    }}
                  >
                    Employee ID
                  </TableCell>
                  <TableCell
                    sx={{
                      color: themes?.generalText || "#2D3748",
                      fontWeight: 600,
                      backgroundColor: themes?.sidebarDivider,
                    }}
                  >
                    Employee Name
                  </TableCell>
                  <TableCell
                    sx={{
                      color: themes?.generalText || "#2D3748",
                      fontWeight: 600,
                      backgroundColor: themes?.sidebarDivider,
                    }}
                  >
                    Amount
                  </TableCell>
                  <TableCell
                    sx={{
                      color: themes?.generalText || "#2D3748",
                      fontWeight: 600,
                      backgroundColor: themes?.sidebarDivider,
                      width: 60,
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      color: themes?.generalText || "#2D3748",
                      fontWeight: 600,
                      backgroundColor: themes?.sidebarDivider,
                    }}
                  >
                    Notes
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importResults.map((result, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "rgba(0, 0, 0, 0.02)",
                      },
                      backgroundColor:
                        result.status === "error"
                          ? "rgba(244, 67, 54, 0.05)"
                          : "transparent",
                    }}
                  >
                    <TableCell sx={{ py: 1, fontSize: "0.85rem" }}>
                      {result.row}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      {result.data?.[0] || "-"}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      {result.data?.[1] || "-"}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      {result.data?.[2] || "-"}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {result.status === "success" ? (
                        <CheckCircle sx={{ fontSize: 18, color: "#4caf50" }} />
                      ) : (
                        <Error sx={{ fontSize: 18, color: "#f44336" }} />
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color:
                            result.status === "success" ? "#4caf50" : "#f44336",
                          textTransform: "capitalize",
                        }}
                      >
                        {result.status}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1,
                        fontSize: "0.85rem",
                        color:
                          result.status === "error"
                            ? "#d32f2f"
                            : "text.secondary",
                      }}
                    >
                      {result.message}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Action buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mt: 4,
          flexWrap: "wrap",
        }}
      >
        <CustomButton
          actionName={
            importStatus === "completed"
              ? "Go to payroll run"
              : importStatus === "error"
                ? "Try Again"
                : "Importing..."
          }
          actionFuntion={onClose}
          disableAction={importStatus === "importing"}
          loading={importStatus === "importing"}
          customTitleSx={{ textTransform: "none", fontSize: "0.95rem" }}
        />
      </Box>
    </Box>
  );
};
